import { Injectable } from '@angular/core';

export type Point = [number, number];
export type GeoJsonPolygon = {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: number[][][] | number[][][][];
};

@Injectable({
  providedIn: 'root'
})
export class ElectronicFieldPolygonService {
  
  private readonly LAT_MIN = -90;
  private readonly LAT_MAX = 90;
  private readonly LNG_MIN = -180;
  private readonly LNG_MAX = 180;
  private readonly EARTH_RADIUS_KM = 6371;

  /**
   * Парсит полигон из различных форматов (WKT, GeoJSON, массив)
   */
  parsePolygonData(polygonJson: unknown): Point[] | null {
    try {
      if (!polygonJson) {
        return null;
      }

      // Обработка строковых форматов
      if (typeof polygonJson === 'string') {
        return this.parseStringPolygon(polygonJson);
      }

      // Если это массив, рекурсивно обрабатываем элементы
      if (Array.isArray(polygonJson)) {
        return this.parseArrayPolygon(polygonJson);
      }

      // Если это объект, пытаемся распарсить как GeoJSON
      if (typeof polygonJson === 'object' && polygonJson !== null) {
        return this.parseGeoJSONPolygon(polygonJson as Record<string, unknown>);
      }

      return null;
    } catch {
      return null;
    }
  }

  private parseStringPolygon(str: string): Point[] | null {
    const trimmed = str.trim();
    
    if (trimmed.startsWith('MULTIPOLYGON')) {
      return this.parseWKTMultiPolygon(trimmed);
    }
    
    if (trimmed.startsWith('POLYGON')) {
      return this.parseWKTPolygon(trimmed);
    }

    // Пробуем распарсить как JSON строку
    try {
      const parsed = JSON.parse(str);
      return this.parseGeoJSONPolygon(parsed);
    } catch {
      return null;
    }
  }

  private parseArrayPolygon(arr: unknown[]): Point[] | null {
    const allPoints: Point[] = [];
    
    for (const item of arr) {
      const parsed = this.parsePolygonData(item);
      if (parsed?.length) {
        allPoints.push(...parsed);
      }
    }
    
    return allPoints.length ? allPoints : null;
  }

  private parseWKTPolygon(wktString: string): Point[] | null {
    const coordsMatch = wktString.match(/POLYGON\s*\(\(([^)]+)\)\)/);
    if (!coordsMatch?.[1]) {
      return null;
    }

    const points = this.parseWKTCoordinates(coordsMatch[1]);
    return points.length ? points : null;
  }

  private parseWKTMultiPolygon(wktString: string): Point[] | null {
    const polygonMatches = wktString.match(/\(\([^)]+\)\)/g);
    if (!polygonMatches?.length) {
      return null;
    }

    const allPoints: Point[] = [];
    
    for (const polygonMatch of polygonMatches) {
      const coordsString = polygonMatch.replace(/^\(\(|\)\)$/g, '');
      const points = this.parseWKTCoordinates(coordsString);
      allPoints.push(...points);
    }

    return allPoints.length ? allPoints : null;
  }

  private parseWKTCoordinates(coordString: string): Point[] {
    const points: Point[] = [];
    const coordPairs = coordString.split(',');
    
    for (const pair of coordPairs) {
      const parts = pair.trim().split(/\s+/);
      if (parts.length >= 2) {
        const lng = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        
        if (this.isValidCoordinate(lng, lat)) {
          points.push([lng, lat]);
        }
      }
    }
    
    return points;
  }

  private parseGeoJSONPolygon(data: Record<string, unknown>): Point[] | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    const type = data['type'];
    const coordinates = data['coordinates'];

    if (!coordinates || !Array.isArray(coordinates)) {
      return null;
    }

    if (type === 'MultiPolygon') {
      return this.parseMultiPolygonCoordinates(coordinates);
    }
    
    if (type === 'Polygon') {
      return this.parsePolygonCoordinates(coordinates);
    }

    return null;
  }

  private parseMultiPolygonCoordinates(coords: unknown[]): Point[] | null {
    const allPoints: Point[] = [];
    
    if (!Array.isArray(coords)) {
      return null;
    }
    
    for (const polygon of coords) {
      if (Array.isArray(polygon) && polygon[0] && Array.isArray(polygon[0])) {
        const ring = polygon[0] as unknown[];
        for (const coord of ring) {
          if (Array.isArray(coord) && coord.length >= 2) {
            const [lng, lat] = coord;
            if (this.isValidCoordinate(Number(lng), Number(lat))) {
              allPoints.push([Number(lng), Number(lat)]);
            }
          }
        }
      }
    }
    
    return allPoints.length ? allPoints : null;
  }

  private parsePolygonCoordinates(coords: unknown[]): Point[] | null {
    if (!Array.isArray(coords) || !coords.length) {
      return null;
    }
    
    const ring = Array.isArray(coords[0]) ? coords[0] : coords;
    
    const points: Point[] = [];
    for (const coord of ring as unknown[]) {
      if (Array.isArray(coord) && coord.length >= 2) {
        const [lng, lat] = coord;
        if (this.isValidCoordinate(Number(lng), Number(lat))) {
          points.push([Number(lng), Number(lat)]);
        }
      }
    }
    
    return points.length ? points : null;
  }

  private isValidCoordinate(lng: number, lat: number): boolean {
    return !isNaN(lat) && !isNaN(lng) &&
           lat >= this.LAT_MIN && lat <= this.LAT_MAX &&
           lng >= this.LNG_MIN && lng <= this.LNG_MAX;
  }

  /**
   * Валидирует и фильтрует координаты
   */
  validateCoordinates(points: Point[]): Point[] {
    return points.filter(point => {
      if (!Array.isArray(point) || point.length < 2) {
        return false;
      }
      const [lng, lat] = point;
      return this.isValidCoordinate(lng, lat);
    });
  }

  /**
   * Преобразует координаты из формата [lng, lat] в [lat, lng] для Leaflet
   */
  toLeafletCoords(points: Point[]): [number, number][] {
    return points
      .filter(point => Array.isArray(point) && point.length >= 2)
      .map(point => {
        const [lng, lat] = point;
        return [Number(lat), Number(lng)] as [number, number];
      });
  }

  /**
   * Проверяет, находится ли точка внутри полигона (алгоритм ray casting)
   */
  isPointInPolygon(point: Point, polygon: Point[]): boolean {
    if (!polygon.length) {
      return false;
    }

    const [x, y] = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];

      const intersect = ((yi > y) !== (yj > y)) &&
                       (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      
      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  }

  /**
   * Вычисляет расстояние между двумя точками на сфере (формула гаверсинуса)
   */
  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return this.EARTH_RADIUS_KM * c;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }
}